import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dialogsAPI, userAPI } from '../services/api';
import { useAuth } from '../services/AuthContext';
import ReactMarkdown from 'react-markdown';
import CoachSidebar from '../components/Dashboard/CoachSidebar';
import EditAvatarModal from '../components/Modals/EditAvatarModal';
import EditPromptModal from '../components/Modals/EditPromptModal';

const DialogHistory = () => {
  const { dialogId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [linkedCoach, setLinkedCoach] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [messagesRes, dialogRes, coachesRes] = await Promise.all([
          dialogsAPI.getMessages(dialogId),
          dialogsAPI.getDialog(dialogId),
          user?.role === 'client' ? userAPI.getMyCoaches() : Promise.resolve({ data: [] })
        ]);

        // Process Messages
        const items = Array.isArray(messagesRes.data) ? messagesRes.data : (messagesRes.data?.items || []);
        const normalized = items.map(m => ({
          id: m.id,
          text: m.content || m.text,
          sender: m.role || m.sender,
          timestamp: new Date(m.created_at || m.timestamp)
        }));
        normalized.sort((a, b) => a.timestamp - b.timestamp);
        setMessages(normalized);

        // Process Dialog & Coach
        if (dialogRes.data) {
          if (user?.role === 'client') {
            const coach = coachesRes.data.find(c => c.chatId === dialogRes.data.coach_id);
            setLinkedCoach(coach);
          }
        }
      } catch (err) {
        console.error('Failed to fetch dialog data:', err);
        setError('Could not load conversation history. It might have been deleted or moved.');
      } finally {
        setIsLoading(false);
      }
    };

    if (dialogId) fetchData();
  }, [dialogId, user]);

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    return timestamp.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="relative flex size-full min-h-screen flex-col bg-[var(--bg-main)] group/design-root overflow-x-hidden font-display text-[var(--text-primary)]">
      <div className="layout-container flex h-full grow flex-col">
        <div className="px-4 sm:px-6 md:px-8 lg:px-12 flex flex-1 justify-center py-5 w-full">
          <div className="layout-content-container flex flex-col lg:flex-row w-full max-w-[1200px] flex-1 gap-8">
            
            <main className="flex-1 flex flex-col min-h-0">
              <header className="mb-6 flex items-center justify-between">
                <div>
                  <button 
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--tangerine)] transition-colors mb-2 text-xs font-black uppercase tracking-widest"
                  >
                    <span className="material-symbols-outlined text-sm">arrow_back</span>
                    {user?.role === 'coach' ? 'Back' : 'Back to Coaches'}
                  </button>
                  <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] tracking-tight">Conversation Transcript</h1>
                </div>
                
                <div className="flex items-center gap-4">
                  {user?.role === 'client' && linkedCoach && (
                    <div className="flex gap-2">
                       <button
                        onClick={() => navigate(`/user/chat/${linkedCoach.id}/${dialogId}`)}
                        className="px-4 py-2 bg-[var(--surface-white)] border border-[var(--border-light)] hover:border-[var(--tangerine)] text-[var(--text-primary)] hover:text-[var(--tangerine)] rounded-xl text-xs font-bold flex items-center gap-2 transition-all shadow-sm"
                      >
                        <span className="material-symbols-outlined text-sm">chat</span>
                        Continue in Chat
                      </button>
                      <button
                        onClick={() => navigate(`/user/voice/${linkedCoach.id}`)}
                        className="px-4 py-2 bg-[var(--surface-white)] border border-[var(--border-light)] hover:border-[var(--tangerine)] text-[var(--text-primary)] hover:text-[var(--tangerine)] rounded-xl text-xs font-bold flex items-center gap-2 transition-all shadow-sm"
                      >
                        <span className="material-symbols-outlined text-sm">mic</span>
                        Voice
                      </button>
                      <button
                        onClick={() => navigate(`/user/video/${linkedCoach.id}`)}
                        className="px-4 py-2 bg-[var(--tangerine)] text-white hover:bg-[#d97700] rounded-xl text-xs font-bold flex items-center gap-2 transition-all shadow-md shadow-orange-500/20"
                      >
                        <span className="material-symbols-outlined text-sm">videocam</span>
                        Video
                      </button>
                    </div>
                  )}

                  {messages.length > 0 && (
                    <div className="px-4 py-2 bg-[var(--off-white)] rounded-2xl border border-[var(--border-light)] text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-widest">
                      {formatDate(messages[0].timestamp)}
                    </div>
                  )}
                </div>
              </header>

              <div className="bg-[var(--surface-white)] shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-[2.5rem] flex flex-col overflow-hidden h-[750px] relative">
                <div className="flex-1 overflow-y-auto px-6 py-8 space-y-6 scroll-smooth custom-scrollbar">
                  {isLoading ? (
                    <div className="flex flex-col items-center justify-center h-full opacity-40">
                      <div className="w-12 h-12 border-4 border-[var(--tangerine)] border-t-transparent rounded-full animate-spin mb-4"></div>
                      <p className="font-black uppercase tracking-[0.2em] text-xs">Retrieving transcript...</p>
                    </div>
                  ) : error ? (
                    <div className="flex flex-col items-center justify-center h-full text-center p-8">
                      <span className="material-symbols-outlined text-5xl text-red-400 mb-4">error</span>
                      <p className="text-[var(--text-secondary)] font-bold mb-4">{error}</p>
                      <button 
                        onClick={() => navigate(-1)}
                        className="bg-[var(--tangerine)] text-white px-8 py-3 rounded-2xl font-bold shadow-lg"
                      >
                        Go Back
                      </button>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full opacity-40 text-center">
                      <span className="material-symbols-outlined text-5xl mb-4">chat_bubble_outline</span>
                      <p className="font-black uppercase tracking-[0.2em] text-xs">No messages in this conversation</p>
                    </div>
                  ) : (
                    messages.map((message, index) => {
                      const isUser = message.sender === 'user';
                      
                      return (
                        <div key={message.id || index} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                          <div className={`flex ${isUser ? 'flex-row-reverse' : 'flex-row'} items-end gap-3 max-w-[85%]`}>
                            <div className={`group relative px-6 py-4 rounded-[2rem] text-sm shadow-sm ${isUser ? 'bg-[var(--tangerine)] text-white rounded-br-none' : 'bg-[var(--off-white)] text-[var(--text-primary)] border border-[var(--border-light)] rounded-bl-none'}`}>
                              <div className="markdown-content leading-relaxed">
                                <ReactMarkdown components={{ p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p> }}>{message.text}</ReactMarkdown>
                              </div>
                              <p className={`text-[10px] font-bold mt-2 uppercase tracking-widest ${isUser ? 'text-white/60' : 'text-[var(--text-secondary)]/60'}`}>{formatTime(message.timestamp)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </main>
            
            {user?.role === 'coach' && (
              <CoachSidebar 
                onOpenAvatarModal={() => setIsAvatarModalOpen(true)}
                onOpenPromptModal={() => setIsPromptModalOpen(true)}
                onOpenSettingsModal={() => { window.location.href = '/coach/settings' }}
              />
            )}

            <EditAvatarModal isOpen={isAvatarModalOpen} onClose={() => setIsAvatarModalOpen(false)} currentAvatar="" />
            <EditPromptModal isOpen={isPromptModalOpen} onClose={() => setIsPromptModalOpen(false)} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DialogHistory;
